/**
 * Connector Error Messages
 * 
 * User-friendly error message mapping for connector-related API errors.
 */

/**
 * Map of API error codes to user-friendly messages
 */
const CONNECTOR_ERROR_MESSAGES: Record<string, string> = {
  // Stripe errors
  invalid_api_key: 'The Stripe API key is invalid. Please check and try again.',
  stripe_auth_failed: 'Failed to authenticate with Stripe. Please verify your API key.',
  stripe_rate_limit: 'Too many requests to Stripe. Please wait a moment and try again.',
  stripe_connection_error: 'Could not connect to Stripe. Please check your internet connection.',
  
  // Foxy errors
  invalid_store_url: 'The Foxy store URL is invalid. Please check the format.',
  foxy_auth_failed: 'Failed to authenticate with Foxy. Please verify your encryption key.',
  webhook_setup_failed: 'Failed to set up webhook. Please try again.',
  invalid_encryption_key: 'The encryption key format is invalid.',
  
  // Meta OAuth errors
  meta_auth_failed: 'Failed to authenticate with Meta. Please try connecting again.',
  meta_permissions_denied: 'Required permissions were not granted. Please reconnect and accept all requested permissions.',
  meta_token_expired: 'Your Meta access token has expired. Please reconnect your account.',
  meta_token_invalid: 'Your Meta access token is no longer valid. Please reconnect your account.',
  meta_rate_limit: 'Too many requests to Meta. Please wait a moment and try again.',
  meta_app_not_configured: 'Meta app configuration error. Please contact support.',
  meta_account_deauthorized: 'Your Meta account access was revoked. Please reconnect.',
  meta_no_ad_accounts: 'No ad accounts found for this Meta account.',
  access_denied: 'Access was denied. Please try again and grant the required permissions.',
  user_denied: 'You declined the authorization request. Please try again if this was unintentional.',

  // Google OAuth errors
  google_auth_failed: 'Failed to authenticate with Google. Please try connecting again.',
  google_token_expired: 'Your Google access token has expired. Please reconnect your account.',
  google_permissions_denied: 'Required Google permissions were not granted. Please reconnect and accept all requested permissions.',

  // Generic errors
  network_error: 'Network error. Please check your connection and try again.',
  server_error: 'Server error. Please try again later.',
  unauthorized: 'You are not authorized to perform this action.',
  not_found: 'Configuration not found.',
  already_configured: 'This provider is already configured.',

  // Default
  unknown: 'An unexpected error occurred. Please try again.',
}

/**
 * Get a user-friendly error message from an API error
 */
export function getConnectorErrorMessage(error: unknown): string {
  if (!error) return CONNECTOR_ERROR_MESSAGES.unknown
  
  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    
    // Try to match known error patterns
    if (message.includes('invalid api key') || message.includes('invalid_api_key')) {
      return CONNECTOR_ERROR_MESSAGES.invalid_api_key
    }
    if (message.includes('unauthorized') || message.includes('401')) {
      return CONNECTOR_ERROR_MESSAGES.unauthorized
    }
    if (message.includes('not found') || message.includes('404')) {
      return CONNECTOR_ERROR_MESSAGES.not_found
    }
    if (message.includes('network') || message.includes('fetch')) {
      return CONNECTOR_ERROR_MESSAGES.network_error
    }
    if (message.includes('500') || message.includes('server')) {
      return CONNECTOR_ERROR_MESSAGES.server_error
    }
    // Meta-specific error patterns
    if (message.includes('access_denied') || message.includes('user denied') || message.includes('user_denied')) {
      return CONNECTOR_ERROR_MESSAGES.access_denied
    }
    if (message.includes('token expired') || message.includes('oauthexception')) {
      return CONNECTOR_ERROR_MESSAGES.meta_token_expired
    }
    if (message.includes('permissions') || message.includes('scope')) {
      return CONNECTOR_ERROR_MESSAGES.meta_permissions_denied
    }
    if (message.includes('deauthorized') || message.includes('revoked')) {
      return CONNECTOR_ERROR_MESSAGES.meta_account_deauthorized
    }
    if (message.includes('rate limit') || message.includes('too many')) {
      return CONNECTOR_ERROR_MESSAGES.meta_rate_limit
    }
    
    // If error message is user-friendly enough, return it
    if (error.message.length < 100 && !error.message.includes('HTTP')) {
      return error.message
    }
    
    return CONNECTOR_ERROR_MESSAGES.unknown
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    if (CONNECTOR_ERROR_MESSAGES[error]) {
      return CONNECTOR_ERROR_MESSAGES[error]
    }
    if (error.length < 100) {
      return error
    }
  }
  
  return CONNECTOR_ERROR_MESSAGES.unknown
}

/**
 * Format a provider name for display
 */
export function formatProviderName(provider: string): string {
  const names: Record<string, string> = {
    stripe: 'Stripe',
    foxy: 'Foxy',
    shopify: 'Shopify',
    polar: 'Polar',
    ga4: 'Google Analytics',
    meta: 'Meta Ads',
  }
  return names[provider.toLowerCase()] || provider
}


